import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { useTranslations } from '@vp/om-translations';
import { useQuery } from 'react-query';
import { BasicPreloader } from '../shared/BasicPreloader';
import useSiteData from '../../hooks/useSiteData';
import DebugTools from '../debugTooling/DebugTools';
import { orderPlacedEventIfDataPresent } from '../../apis/trackingApi';
import { OrderDetailsContext } from '../contexts/OrderDetailsContext';
import { OrderDetailsDisplay } from './OrderDetailsDisplay';
import ErrorHandler from '../shared/ErrorHandler';
import { formatFractionDigits } from '../../util/fractionDigitsFormatter';
import { orderPlaced } from '../../apis/pricingContextApi';
import {
  GET_ORDER_FEEDBACK_GROUPS,
  GET_SHOPPER_ORDER_DETAILS,
} from '../../constants/reactQueryKeys';
import { saveVatIdToShopperAccount } from '../../apis/vatIdApi';
import useIdentityContext from '../../hooks/useIdentityContext';
import { SourcePageContext } from '../contexts/SourcePageContext';
import PrintLayout from './styles/printLayout';
import { getTaxInclusiveAndExclusivePrices } from '../../util/taxDisplayPricingHelpers';
import { DebugModeContext } from '../contexts/DebugModeContext';
import { getOrderDetails } from '../../apis/bopApi';
import { getFeedbackGroupsForOrder } from '../../apis/orderUpdateRequestsApi';
import { customerFeedbackGroupStatesFilter } from '../../constants/customerFeedbackGroupStates';
import { customerFeedbackTypesFilter } from '../../constants/customerFeedbackTypes';
import { CustomerFeedbackGroupsProvider } from '../contexts/CustomerFeedbackGroupsProvider';
import { ExpressCheckoutContextProvider } from '../contexts/ExpressCheckoutContext';

export const OrderDetailsApplication = ({ shopperId, orderId }) => {
  const { vistacartTenant, tenantEnvironment, siteName } = useSiteData();
  const {
    identity: { isShopper },
  } = useIdentityContext();
  const { isConfirmationPage: confirmationMode } =
    useContext(SourcePageContext);

  const [isSmsSubscriptionModalOpen, setIsSmsSubscriptionModalOpen] =
    useState(confirmationMode);

  const {
    isLoading,
    isError,
    data: order,
    error,
  } = useQuery([GET_SHOPPER_ORDER_DETAILS, orderId, shopperId], async () => {
    const orderDetails = await getOrderDetails({
      orderId,
      shopperId,
      environment: tenantEnvironment,
    });
    return orderDetails;
  });

  const { data: feedbackGroups } = useQuery(
    [GET_ORDER_FEEDBACK_GROUPS, orderId, shopperId],
    async () =>
      getFeedbackGroupsForOrder({
        orderNumber: orderId,
        shopperId,
        states: customerFeedbackGroupStatesFilter,
        types: customerFeedbackTypesFilter,
        environment: tenantEnvironment,
      }) || [],
    {
      enabled: !!orderId && !!shopperId,
    }
  );

  const { debugEnabled } = useContext(DebugModeContext);
  const [orderSummaryMessages] = useTranslations(['orderSummaryMessages']);

  // TODO: We will only want to fire this event if this is a shopper session.
  useEffect(() => {
    const orderCompletePostProcessing = async () => {
      if (isShopper) {
        await saveVatIdToShopperAccount(orderId, siteName);
      }
      orderPlacedEventIfDataPresent(orderId, shopperId);
    };
    if (confirmationMode && shopperId) {
      orderCompletePostProcessing();
    }
  }, [confirmationMode, isShopper, orderId, shopperId, siteName]);

  useEffect(() => {
    if (confirmationMode) {
      // fire pcxtV3.orderPlaced method
      orderPlaced(vistacartTenant, shopperId);
    }
  }, [confirmationMode, shopperId, vistacartTenant]);

  const orderNumber = order?.orderNumber;
  const fractionDigits = formatFractionDigits(get(order, 'fractionalDigits'));

  /* Bucket lineItem prices into itemPrices, shippingPrices, and itemGroupPrices and with tax exclusive / inclusive price arrays.
   * These maps of inclusive/exclusive pricing objects are stored in OrderDetailsContext and used as formatted input for tax aware price display */
  const {
    itemIdToTaxInclusiveAndExclusivePricesMap,
    itemGroupIdToTaxInclusiveAndExclusivePricesMap,
    itemIdToTaxInclusiveAndExclusiveShippingPricesMap,
  } = getTaxInclusiveAndExclusivePrices(order);

  /**
   * If we have shipping address and shipping method, we should show ShipmentDetails
   */
  const showShipmentDetails =
    !!order?.shippingAddress && !!order?.orderShippingData;

  const orderDetailsContextValues = useMemo(
    () => ({
      orderNumber,
      vistacartTenantName: vistacartTenant,
      shopperId,
      tenantEnvironment,
      currency: get(order, 'currency') || 'EUR',
      fractionDigits,
      order,
      itemIdToTaxInclusiveAndExclusivePricesMap,
      itemGroupIdToTaxInclusiveAndExclusivePricesMap,
      itemIdToTaxInclusiveAndExclusiveShippingPricesMap,
      showShipmentDetails,
      isSmsSubscriptionModalOpen,
      setIsSmsSubscriptionModalOpen,
    }),
    [
      fractionDigits,
      itemGroupIdToTaxInclusiveAndExclusivePricesMap,
      itemIdToTaxInclusiveAndExclusivePricesMap,
      order,
      orderNumber,
      shopperId,
      itemIdToTaxInclusiveAndExclusiveShippingPricesMap,
      tenantEnvironment,
      vistacartTenant,
      showShipmentDetails,
      isSmsSubscriptionModalOpen,
      setIsSmsSubscriptionModalOpen,
    ]
  );

  if (isError) {
    return <ErrorHandler error={error} pageMessages={orderSummaryMessages} />;
  }

  if (isLoading) {
    return <BasicPreloader isCentered size="super" my={9} />;
  }

  return (
    <CustomerFeedbackGroupsProvider feedbackGroups={feedbackGroups}>
      <OrderDetailsContext.Provider value={orderDetailsContextValues}>
        {debugEnabled && (
          <DebugTools shopperId={shopperId} order={orderDetailsContextValues} />
        )}
        <ExpressCheckoutContextProvider>
          <PrintLayout />
          <OrderDetailsDisplay />
        </ExpressCheckoutContextProvider>
      </OrderDetailsContext.Provider>
    </CustomerFeedbackGroupsProvider>
  );
};

OrderDetailsApplication.propTypes = {
  shopperId: PropTypes.string.isRequired,
  orderId: PropTypes.string.isRequired,
};
